import axios, { AxiosRequestConfig } from "axios";

// Helper function to handle token refresh for both axios and streaming responses
export const handleTokenRefresh = (headers: any, responseData?: any) => {
	// Check for token refresh headers (try all possible case variations)
	let newToken = headers['x-new-token'] || headers['X-New-Token'] || 
				  headers['x-new-token'.toLowerCase()] || headers['X-NEW-TOKEN'];
	let tokenRefreshed = headers['x-token-refreshed'] || headers['X-Token-Refreshed'] || 
					    headers['x-token-refreshed'.toLowerCase()] || headers['X-TOKEN-REFRESHED'];

	// Alternative: Check if token is in response body (fallback for CORS issues)
	if (!newToken && responseData) {
		newToken = responseData['x-new-token'] || responseData.newToken || responseData.token;
		tokenRefreshed = responseData['x-token-refreshed'] || responseData.tokenRefreshed;
	}

	if (newToken && (tokenRefreshed === 'true' || tokenRefreshed === true)) {
		console.log('✅ Token refresh confirmed! Updating token from:', localStorage.getItem("token")?.substring(0, 20) + '...', 'to:', newToken?.substring(0, 20) + '...');
		
		// Update token in localStorage
		localStorage.setItem("token", newToken);
		
		console.log('✅ Token updated in localStorage successfully');
		
		// Dispatch an event to update auth context
		window.dispatchEvent(new CustomEvent('tokenRefreshed', { detail: { newToken } }));
		
		return true; // Token was refreshed
	}
	
	return false; // No token refresh occurred
};

// Helper function to process streaming response headers
export const processStreamingHeaders = (response: Response) => {
	// Convert Headers object to plain object for easier processing
	const headers: any = {};
	response.headers.forEach((value, key) => {
		headers[key] = value;
	});
	
	console.log('🔄 Processing streaming response headers:', headers);
	return handleTokenRefresh(headers);
};

const createAxiosInstance = (
	customHeaders: AxiosRequestConfig["headers"] = {}
) => {
	const instance = axios.create({
		baseURL:
			process.env.REACT_APP_SERVER_URL || "https://chat.1ciso.io/backend",
		headers: customHeaders,
	});

	// Request interceptor to add fresh token to every request
	instance.interceptors.request.use(
		(config) => {
			const token = localStorage.getItem("token");
			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
				console.log('Using token for request:', token.substring(0, 20) + '...');
			} else {
				console.log('No token found in localStorage');
			}
			return config;
		},
		(error) => {
			return Promise.reject(error);
		}
	);

	// Response interceptor to handle token refresh
	instance.interceptors.response.use(
		(response) => {
			// Debug: Log all response headers to see what we're getting
			console.log('Response headers:', response.headers);
			
			// For streaming responses, headers might need to be accessed differently
			let headers: any = {};
			if (typeof response.headers.toJSON === 'function') {
				headers = response.headers.toJSON();
				console.log('Headers as JSON:', headers);
			} else {
				headers = response.headers;
			}
			
			// Use the helper function to handle token refresh
			handleTokenRefresh(headers, response.data);

			return response;
		},
		(error) => {
			// Handle response errors
			console.log('Response error:', error);
			return Promise.reject(error);
		}
	);

	return instance;
};

export default createAxiosInstance;